/**
 * HomeModule -- Widget-based configurable dashboard.
 *
 * Uses WidgetEngine to render 18 configurable widgets with per-widget
 * 3-dot menus for move/resize/hide. Layout persisted to localStorage.
 *
 * Widgets: GreetingBar, Weather, PrayerCountdown, QuickActions, DailyQuest,
 * StatsRow, DailyVerse, MemorizProgress, KidsRadar, MoneySnapshot,
 * ActivityFeed, Recommendations, CommunityPulse, SeasonalFood,
 * RecentOrders, ToolsLinks, ReadingProgress, RemindersWidget.
 */

import type { AweniContext, AweniModule } from '../../types';
import type { WidgetContext } from './types';
import { WidgetEngine } from './WidgetEngine';
import { LocationService, getLocationService } from '../../services/LocationService';
import { WeatherService } from '../../services/WeatherService';
import { prayerTimeService } from '../../services/PrayerTimeService';
import { HomeHero } from './HomeHero';
import { ServiceGrid } from './ServiceGrid';
import { LocationPrimingCard } from './LocationPrimingCard';
import { AtAGlanceStrip } from './AtAGlanceStrip';
import { PrayerScheduleStrip } from './PrayerScheduleStrip';
import {
  // 2026-04-30 daily-habits redesign:
  // KEPT (each answers a daily check):
  //   - DailyVerse        → "spiritual touch" (top of feed)
  //   - NewsWidget        → "what happened today"
  //   - MemorizProgress   → "Quran continue" (resume strip)
  //   - MoneySnapshot     → "did I overspend?"
  //   - ActivityFeed      → family activity (filtered)
  //   - Recommendations   → discover content
  // CUT (clutter, rare check, or 0-state failure signal):
  //   - DailyQuest        → game mechanic, not a daily check (deferred to Memoriz tile)
  //   - StatsRow          → belongs in Profile, not home
  //   - KidsRadar         → only useful to parents w/ kids; defer to Family module
  //   - CommunityPulse    → episodic, fold into Recommendations later
  //   - SeasonalFood      → surface only inside SelectO
  //   - RecentOrders      → surface only inside SelectO
  //   - ReadingProgress   → MemorizProgress already covers it
  //   - RemindersWidget   → smart Tasks pill (future) replaces it
  //   - WatchWidget       → premature until Watch service has DAU
  // Above-the-fold (Hero + Grid + Hijri ribbon) handles GreetingBar,
  // PrayerCountdown, QuickActions, ToolsLinks, ClaimBanner. The Hero shows
  // a one-line city·temp·icon glance; the rich WeatherWidget (sparkline +
  // tap → /weather) renders below in the feed.
  ClaimBanner,
  // PrayerCountdown / WeatherWidget / CalendarTimePair are NOT imported
  // here — their info is rendered as clickable chips in HomeHero (single
  // source of truth, no duplicate cards). They remain exported from
  // ./widgets and are still callable from anywhere if a future surface
  // needs them.
  DailyVerse,
  NewsWidget,
  MemorizProgress,
  MoneySnapshot,
  ActivityFeed,
  Recommendations,
  AroundMeWidget,
  YawmiWidget,
  // Additional developed widgets enabled 2026-05-06: each has its own
  // visibilityRule so it self-hides when there's no data; failures are
  // isolated by WidgetEngine and never propagate to siblings.
  KidsRadar,
  StatsRow,
  RemindersWidget,
  RecentOrders,
  ReadingProgress,
  CommunityPulse,
  DailyQuest,
  SeasonalFood,
} from './widgets';

export default class HomeModule implements AweniModule {
  private container: HTMLElement | null = null;
  private context: AweniContext | null = null;
  private engine: WidgetEngine | null = null;
  private hero: HomeHero | null = null;
  private grid: ServiceGrid | null = null;
  private glance: AtAGlanceStrip | null = null;
  private prayerStrip: PrayerScheduleStrip | null = null;
  private locationService: LocationService;
  private weatherService: WeatherService;
  private locUnsub: (() => void) | null = null;
  private wxUnsub: (() => void) | null = null;

  constructor() {
    // Singleton — every widget/header/module that imports getLocationService()
    // shares this same instance + listener bus. No more split listener arrays.
    this.locationService = getLocationService();
    this.weatherService = new WeatherService(this.locationService);
  }

  async mount(container: HTMLElement, context: AweniContext): Promise<void> {
    this.container = container;
    this.context = context;

    const t = context.i18n.t.bind(context.i18n);
    const isRtl = context.i18n.dir === 'rtl';
    const locale = context.i18n.locale || 'en';
    const userName = context.auth.user?.name?.split(' ')[0] || '';
    const isGuest = !!context.auth.user?.is_guest;

    // SUPER AWENI mount strategy: SYNCHRONOUS skeleton + render widgets
    // immediately with cached data, then refresh data AFTER first paint via
    // requestIdleCallback. This guarantees mount() never blocks on third-
    // party APIs (open-meteo) or geolocation prompts — both of which were
    // observed to stall the import chain on Firefox + ETP and trigger
    // load-shedding cancellation of in-flight chunks.
    this.injectStyles();
    container.innerHTML = this.buildSkeleton(isRtl);

    // Build widget context. Uses whatever data is already in localStorage
    // (LocationService loads from storage in its constructor, so getState()
    // returns cached coords on a returning user, or {lat:null,lng:null} on
    // a fresh user). Widgets gracefully handle missing data.
    const widgetContext: WidgetContext = {
      t,
      locale,
      isRtl,
      isDark: context.theme.isDark,
      userName,
      isGuest,
      navigate: (moduleId: string, path?: string) => context.navigate(moduleId, path),
      showNotification: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => context.showNotification(msg, type),
      getLocationService: () => this.locationService.getState(),
      refreshLocation: async () => {
        // Triggered explicitly by a widget CTA (user opt-in). Safe to
        // request geolocation here — never automatic.
        await this.locationService.refresh();
        const loc = this.locationService.getState();
        if (loc.lat !== null && loc.lng !== null) {
          prayerTimeService.setLocation(loc.lat, loc.lng, loc.city || undefined);
        }
        await this.weatherService.fetch();
        if (this.engine && this.container) {
          const inner = this.container.querySelector('.home-widgets-container') as HTMLElement;
          if (inner) this.engine.render(inner);
        }
      },
      getWeatherData: () => this.weatherService.getData(),
    };

    // If we already have cached coordinates from a previous session, sync
    // them into PrayerTimeService so the prayer widget renders the correct
    // times immediately — without firing any network or geolocation request.
    const cachedLoc = this.locationService.getState();
    if (cachedLoc.lat !== null && cachedLoc.lng !== null) {
      prayerTimeService.setLocation(cachedLoc.lat, cachedLoc.lng, cachedLoc.city || undefined);
    }

    this.engine = new WidgetEngine(widgetContext);

    // Zone 4 — feed widgets (lazy/dynamic). Each one fails silently if its
    // data source is unavailable; one broken widget never breaks the home.
    // Above-the-fold (Hero + Grid) is HANDLED SEPARATELY below as static
    // markup that ALWAYS renders, even if WidgetEngine misbehaves.
    // Lean feed: 8 widgets, ranked by daily-question priority. Order matters
    // — each widget that fails silently leaves no gap above the next one.
    // Hero chips already cover Date / Time / Weather / Prayer — each
    // tappable to open the deep widget (CalendarModal / TimeModal /
    // /weather / /prayer). Therefore PrayerCountdown, WeatherWidget and
    // CalendarTimePair are intentionally NOT in the feed: a single source
    // of truth per piece of info, no redundancy. The widgets remain
    // exported and accessible for use in dedicated module pages.
    // Feed = content widgets only (read / continue / discover / act).
    const feedWidgets = [
      YawmiWidget,        // context CTA: Friday/Eid/Ramadan/birthday → 1-tap wish
      DailyQuest,         // gamification — daily quest of the day
      DailyVerse,         // spiritual touch
      MemorizProgress,    // "where did I stop?" continue strip
      ReadingProgress,    // current blog article continue
      AroundMeWidget,     // country-aware POI strip (mosque/halal in diaspora, pharmacy/café in MENA)
      NewsWidget,         // today's headlines
      MoneySnapshot,      // "did I overspend this week?"
      RecentOrders,       // SelectO recent orders
      KidsRadar,          // family — kids progress
      StatsRow,           // multi-app stats row
      RemindersWidget,    // upcoming reminders
      SeasonalFood,       // seasonal food suggestions
      CommunityPulse,     // community heartbeat
      ActivityFeed,       // family activity (filtered to inner circle)
      Recommendations,    // curated content discover
    ];

    feedWidgets.forEach((w) => this.engine!.register(w));

    // 4-zone layout: Hero + Grid (synchronous, always visible) above
    // the fold; widget feed below for everything dynamic. Between hero
    // and grid, the At-a-Glance strip fuses 2-3 universal daily questions
    // (notifications / prayer / Islamic event) into one compact line.
    container.innerHTML = `
      <div class="home-module px-4 pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">
        <div class="home-claim-banner-container"></div>
        <div class="home-hero-container"></div>
        <div class="home-glance-container"></div>
        <div class="home-grid-container"></div>
        <div class="home-prayer-strip-container"></div>
        <div class="home-widgets-container"></div>
      </div>
    `;

    const claimBannerContainer = container.querySelector('.home-claim-banner-container') as HTMLElement;
    const heroContainer = container.querySelector('.home-hero-container') as HTMLElement;
    const glanceContainer = container.querySelector('.home-glance-container') as HTMLElement;
    const gridContainer = container.querySelector('.home-grid-container') as HTMLElement;
    const prayerStripContainer = container.querySelector('.home-prayer-strip-container') as HTMLElement;
    const widgetsContainer = container.querySelector('.home-widgets-container') as HTMLElement;

    // Render ClaimBanner above the hero — only for guest users. The widget's
    // own visibilityRule re-checks the JWT claim; we keep this thin guard
    // here so we don't even instantiate the DOM for non-guests.
    if (isGuest && (ClaimBanner.visibilityRule?.() ?? true)) {
      claimBannerContainer.style.marginBottom = '12px';
      ClaimBanner.render(claimBannerContainer, 'expanded', widgetContext);
    }

    // IMPORTANT — subscribe to location changes BEFORE mounting children.
    // Listener invocation order in LocationService.notify() is registration
    // order. By registering the HomeModule listener FIRST, prayer-time-service
    // is synced (and stale widget cache is invalidated) BEFORE the hero
    // header / glance strip read prayer state — so they render with fresh
    // data on the very first paint after a city change.
    this.wxUnsub?.();
    this.wxUnsub = this.weatherService.onChange(() => {
      if (this.hero) this.hero.render();
    });

    // Widgets that depend on country/lat data: their DOM is invalidated on
    // every city change so the next engine.render() rebuilds the wrapper
    // (instead of reusing 60s-cached output with the previous country).
    const LOCATION_AWARE_WIDGET_IDS = [
      'money-snapshot',     // currency from countryCode
      'seasonal-food',      // hemisphere from lat
      'news',               // top headlines from country
      'around-me',          // nearby POIs
      'prayer-countdown',   // city label
      'recommendations',    // future regional bias
      'community-pulse',    // future locality bias
      'recent-orders',      // SelectO venues nearby
    ];

    this.locUnsub?.();
    this.locUnsub = this.locationService.onChange(() => {
      // Sync prayer-time service immediately so PrayerCountdown + the
      // At-a-Glance strip + the hero chip all reflect the new coords on
      // the next render frame.
      const loc = this.locationService.getState();
      if (loc.lat !== null && loc.lng !== null) {
        prayerTimeService.setLocation(loc.lat, loc.lng, loc.city || undefined);
      }
      // Force the strips to re-render now that prayer is synced — their
      // own listeners fire before this one (registration order), so without
      // an explicit refresh() they would render with stale prayer data.
      this.glance?.refresh();
      this.prayerStrip?.refresh();
      // Drop stale widget DOM so the next engine.render rebuilds them
      // with the new countryCode / lat. Without this, MoneySnapshot keeps
      // showing "USD" for a Casablanca user because its 60s-cached wrapper
      // was rendered when countryCode was still null.
      this.engine?.invalidateCache(LOCATION_AWARE_WIDGET_IDS);
      // Refresh weather for the new city, then re-render the feed once
      // data lands. Weather fetch is fire-and-forget; widgets render with
      // whatever cached data is available right now.
      this.weatherService.fetch().catch(() => { /* */ }).finally(() => {
        if (this.engine && this.container) {
          const inner = this.container.querySelector('.home-widgets-container') as HTMLElement;
          if (inner) this.engine.render(inner);
        }
      });
    });

    // Mount Hero (zone 1). Synchronous, uses cached data only.
    this.hero = new HomeHero({
      ctx: context,
      locationService: this.locationService,
      weatherService: this.weatherService,
      onChooseCity: () => this._showLocationPriming(context),
      onTapPrayer: () => { /* hash navigation handled by native <a> */ },
      onTapSettings: () => context.navigate('/more/settings'),
    });
    this.hero.mount(heroContainer);

    // Mount At-a-Glance strip (between hero and grid). Self-hides when no
    // data segment is available. The strip listens to LocationService
    // changes internally so it re-renders when the prayer countdown shifts.
    this.glance = new AtAGlanceStrip({ ctx: context });
    this.glance.mount(glanceContainer);

    // Mount Grid (zone 2). 12 doors from doors.config.json, native <a> tags.
    this.grid = new ServiceGrid(context);
    this.grid.mount(gridContainer);

    // Mount the all-prayers-at-a-glance strip below the grid. Self-hides
    // when no location is set; tap → /prayer page.
    this.prayerStrip = new PrayerScheduleStrip({ ctx: context });
    this.prayerStrip.mount(prayerStripContainer);

    // Mount widget feed (zones 3-4). Existing 14 widgets, lazy + tolerant.
    this.engine.render(widgetsContainer);

    this.setupPullToRefresh(container);

    // Schedule background data refresh AFTER first paint. Re-renders Hero
    // when fresh location/weather/prayer data lands.
    this.scheduleBackgroundDataRefresh();
  }

  /** Show the Apple-compliant location priming card. */
  private _showLocationPriming(ctx: AweniContext): void {
    const card = new LocationPrimingCard({
      ctx,
      locationService: this.locationService,
      onResolved: () => {
        // After user picks GPS or city, sync prayer service + re-render hero.
        const loc = this.locationService.getState();
        if (loc.lat !== null && loc.lng !== null) {
          prayerTimeService.setLocation(loc.lat, loc.lng, loc.city || undefined);
          // Also fetch weather now that we have a location.
          this.weatherService.fetch().catch(() => {});
        }
        if (this.hero) this.hero.render();
        if (this.engine && this.container) {
          const inner = this.container.querySelector('.home-widgets-container') as HTMLElement;
          if (inner) this.engine.render(inner);
        }
      },
    });
    card.show();
  }

  /** Refresh location/weather data after first paint, never blocking mount. */
  private scheduleBackgroundDataRefresh(): void {
    const work = async () => {
      try {
        // Only auto-refresh location if browser already granted permission
        // (i.e., on a returning user). Never trigger the prompt automatically.
        let canRefreshLocation = false;
        if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
          try {
            const perm = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
            canRefreshLocation = perm.state === 'granted';
          } catch { /* permissions API not available — skip auto-refresh */ }
        }

        const tasks: Promise<unknown>[] = [];
        if (canRefreshLocation) {
          tasks.push(this.locationService.refresh().catch(() => {}));
        }
        tasks.push(this.weatherService.fetch().catch(() => {}));

        await Promise.all(tasks);

        // Sync prayer-time service with refreshed coords if available
        const loc = this.locationService.getState();
        if (loc.lat !== null && loc.lng !== null) {
          prayerTimeService.setLocation(loc.lat, loc.lng, loc.city || undefined);
        }

        // Re-render Hero with fresh location/weather/prayer.
        if (this.hero) this.hero.render();
        // Re-render widget feed (some widgets like StatsRow / Recommendations
        // depend on fresh data — re-render is cheap with cached state).
        if (this.engine && this.container) {
          const inner = this.container.querySelector('.home-widgets-container') as HTMLElement;
          if (inner) this.engine.render(inner);
        }
      } catch { /* silent — widgets already showed cached/empty state */ }
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => { work(); }, { timeout: 2000 });
    } else {
      setTimeout(() => { work(); }, 50);
    }
  }

  private buildSkeleton(isRtl: boolean): string {
    return `
      <div class="home-module px-4 pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">
        <div class="mb-6">
          <div style="height:28px;width:60%;background:var(--bg-secondary);border-radius:8px;" class="skeleton-pulse"></div>
          <div style="height:14px;width:40%;background:var(--bg-secondary);border-radius:6px;margin-top:8px;" class="skeleton-pulse"></div>
        </div>
        <div class="mb-6">
          <div style="height:60px;width:100%;background:var(--bg-secondary);border-radius:16px;" class="skeleton-pulse"></div>
        </div>
        <div class="mb-6">
          <div style="height:80px;width:100%;background:var(--bg-secondary);border-radius:16px;" class="skeleton-pulse"></div>
        </div>
        <div class="mb-6" style="display:grid;grid-template-columns:repeat(2,1fr);gap:12px;">
          <div style="height:88px;background:var(--bg-secondary);border-radius:16px;" class="skeleton-pulse"></div>
          <div style="height:88px;background:var(--bg-secondary);border-radius:16px;" class="skeleton-pulse"></div>
          <div style="height:88px;background:var(--bg-secondary);border-radius:16px;" class="skeleton-pulse"></div>
          <div style="height:88px;background:var(--bg-secondary);border-radius:16px;" class="skeleton-pulse"></div>
        </div>
        <div class="mb-6" style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;">
          <div style="height:72px;background:var(--bg-secondary);border-radius:16px;" class="skeleton-pulse"></div>
          <div style="height:72px;background:var(--bg-secondary);border-radius:16px;" class="skeleton-pulse"></div>
          <div style="height:72px;background:var(--bg-secondary);border-radius:16px;" class="skeleton-pulse"></div>
        </div>
      </div>
    `;
  }

  private setupPullToRefresh(container: HTMLElement): void {
    let startY = 0;
    let pulling = false;
    const viewport = container.closest('.module-viewport');
    if (!viewport) return;

    viewport.addEventListener('touchstart', (e: Event) => {
      const te = e as TouchEvent;
      if ((viewport as HTMLElement).scrollTop <= 0) {
        startY = te.touches[0].clientY;
        pulling = true;
      }
    }, { passive: true });

    viewport.addEventListener('touchmove', (e: Event) => {
      if (!pulling) return;
      const te = e as TouchEvent;
      const diff = te.touches[0].clientY - startY;
      if (diff > 80) {
        pulling = false;
        if (this.context && this.container) {
          this.unmount();
          this.mount(this.container!, this.context!);
        }
      }
    }, { passive: true });

    viewport.addEventListener('touchend', () => { pulling = false; }, { passive: true });
  }

  private injectStyles(): void {
    if (document.getElementById('home-module-styles')) return;
    const style = document.createElement('style');
    style.id = 'home-module-styles';
    style.textContent = `
      /* ============ HERO (zone 1) ============ */
      .aweni-hero {
        background: var(--card-bg, #FFFFFF);
        border: 1px solid var(--border, #E5E7EB);
        border-radius: 20px;
        padding: 14px 18px 18px;
        margin: 12px 0 16px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .dark .aweni-hero {
        background: linear-gradient(135deg, var(--card-bg, #1E293B) 0%, var(--bg-secondary, #0F172A) 100%);
        border-color: rgba(255,255,255,0.06);
      }
      .aweni-hero-greeting {
        font-size: 18px;
        font-weight: 700;
        color: var(--text-primary, #1E293B);
        margin: 2px 0 2px;
        line-height: 1.3;
      }
      .dark .aweni-hero-greeting { color: #F1F5F9; }
      .aweni-hero-loc-slot { margin: 4px 0 6px; }

      /* ============ HERO CHIPS ROW ============
         Condensed dashboard "complications" — date / time / weather /
         prayer. Each chip is its own clickable surface that opens the
         deep widget (CalendarModal / TimeModal / /weather / /prayer).
         No duplicate cards in the feed below — single source of truth.
         Mobile: 2 cols. Tablet+: 4 cols. */
      .aweni-hero-chips {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        margin: 6px 0 12px;
      }
      @media (min-width: 600px) {
        .aweni-hero-chips { grid-template-columns: repeat(4, 1fr); }
      }
      .aweni-hero-chip {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        min-height: 56px;
        background: var(--card-bg, #FFFFFF);
        border: 1px solid var(--border, #E5E7EB);
        border-radius: 14px;
        color: var(--text-primary, #1E293B);
        text-decoration: none;
        cursor: pointer;
        text-align: start;
        font: inherit;
        transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
      }
      .aweni-hero-chip:hover {
        transform: translateY(-1px);
        border-color: var(--accent, #0A0A0A);
        box-shadow: 0 4px 12px rgba(0,0,0,0.06);
      }
      .aweni-hero-chip:focus-visible {
        outline: 2px solid var(--accent, #0A0A0A);
        outline-offset: 2px;
      }
      .aweni-hero-chip:active { transform: scale(0.98); }
      .aweni-hero-chip-icon {
        font-size: 22px;
        flex-shrink: 0;
        line-height: 1;
      }
      .aweni-hero-chip-body {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        flex: 1;
      }
      .aweni-hero-chip-primary {
        font-size: 13px;
        font-weight: 700;
        color: var(--text-primary, #1E293B);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-variant-numeric: tabular-nums;
      }
      .dark .aweni-hero-chip-primary { color: #F1F5F9; }
      .aweni-hero-chip-secondary {
        font-size: 11px;
        color: #6b6657;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .dark .aweni-hero-chip { background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.10); color: #F1F5F9; }
      .dark .aweni-hero-chip-secondary { color: #94A3B8; }
      /* Pin glyph in the city pill — visually anchors the city to a place. */
      .aweni-hero-pin-glyph { font-size: 12px; opacity: 0.75; }

      /* ============ SERVICE GRID (zone 2) ============ */
      .aweni-service-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 10px;
        margin: 0 0 20px;
      }
      @media (max-width: 360px) {
        .aweni-service-grid { grid-template-columns: repeat(3, 1fr); }
      }
      .aweni-door {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 14px 6px;
        border-radius: 16px;
        background: var(--card-bg, #FFFFFF);
        border: 1px solid var(--border, #E5E7EB);
        color: var(--text-primary, #1E293B);
        text-decoration: none;
        min-height: 88px;
        transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
        -webkit-tap-highlight-color: transparent;
      }
      .dark .aweni-door {
        background: var(--card-bg, #1E293B);
        border-color: rgba(255,255,255,0.06);
        color: var(--text-primary, #F1F5F9);
      }
      .aweni-door:hover { border-color: var(--accent, #0A0A0A); }
      .aweni-door:active { transform: scale(0.96); }
      .dark .aweni-door:active { background: var(--bg-secondary, #0F172A); }
      .aweni-door:focus-visible { outline: 2px solid var(--accent, #0A0A0A); outline-offset: 2px; }
      .aweni-door-icon-wrap {
        width: 48px;
        height: 48px;
        border-radius: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .aweni-door-label {
        font-size: 12px;
        font-weight: 600;
        text-align: center;
        line-height: 1.2;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #1F2937;
      }
      .dark .aweni-door-label { color: #F9FAFB; }

      /* ============ Animations + skeleton ============ */
      @keyframes homeRipple {
        to { width: 300px; height: 300px; opacity: 0; }
      }
      @keyframes skeletonPulse {
        0%, 100% { opacity: 0.4; }
        50% { opacity: 0.8; }
      }
      .skeleton-pulse {
        animation: skeletonPulse 1.2s ease-in-out infinite;
      }
      @keyframes homePulseFire {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.2); }
      }
      .home-pulse-fire {
        display: inline-block;
        animation: homePulseFire 1.5s ease-in-out infinite;
      }
      @keyframes widgetMenuIn {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .widget-wrapper {
        position: relative;
      }
      .widget-menu-btn {
        opacity: 0.4;
        transition: opacity 200ms;
      }
      .widget-wrapper:hover .widget-menu-btn,
      .widget-wrapper:focus-within .widget-menu-btn {
        opacity: 1;
      }
      /* Touch devices: always show menu button */
      @media (hover: none) {
        .widget-menu-btn { opacity: 0.6; }
      }
      /* Ensure minimum touch targets */
      .widget-menu-btn,
      .qa-btn,
      .tool-btn {
        min-height: 44px;
        min-width: 44px;
      }
    `;
    document.head.appendChild(style);
  }

  unmount(): void {
    this.locUnsub?.();
    this.locUnsub = null;
    this.wxUnsub?.();
    this.wxUnsub = null;
    this.hero?.unmount();
    this.hero = null;
    this.glance?.unmount();
    this.glance = null;
    this.prayerStrip?.unmount();
    this.prayerStrip = null;
    this.grid?.unmount();
    this.grid = null;
    if (this.engine) {
      this.engine.destroy();
      this.engine = null;
    }
    if (this.container) {
      this.container.innerHTML = '';
    }
    this.container = null;
    this.context = null;
  }
}
